/**
 * The first name for the greeting ("Hallo Eva,") out of the contact name the invitation carried.
 *
 * The greeting never guesses: no gender, no salutation, no title. A name the rules below cannot read
 * safely gives "" and the page says "Hallo," instead. Titles at the front ("Dr.", "Prof.", "Herr",
 * "Frau") are skipped, not echoed, and never taken for a first name.
 */
const TITLES = new Set([
  "dr", "dr.", "prof", "prof.", "dipl.-ing.", "dipl.-ing", "dipl.-kfm.", "dipl.-kff.", "mag.", "ing.", "m.sc.", "b.sc.", "mba",
  "herr", "frau", "hr.", "fr.", "mr", "mr.", "mrs", "mrs.", "ms", "ms.", "mx", "mx.",
]);

/** A single word made of letters (any script), marks, and inner hyphens or apostrophes - no digits, no dots. */
const NAME_WORD = /^[\p{L}\p{M}][\p{L}\p{M}'’-]*$/u;
const MIN_LENGTH = 2;

export function firstNameOf(contactName: string | undefined | null): string {
  if (!contactName) return "";
  const tokens = contactName.trim().split(/\s+/).filter(Boolean);
  // "Müller, Eva": a comma after the first word means the family name comes first
  if (tokens.length >= 2 && tokens[0].endsWith(",")) {
    const rest = tokens.slice(1).filter((token) => !TITLES.has(token.toLowerCase()));
    return acceptable(rest[0]);
  }
  const words = tokens.filter((token) => !TITLES.has(token.toLowerCase()));
  // a lone word that was a title is no name
  return acceptable(words[0]);
}

/**
 * One rule for every view: a name the server sent ready-made (the status view) is held to the same rule as one
 * read from the contact name (the landing page) - otherwise the page greets "Hallo E2E," in one view and "Hallo,"
 * in another.
 */
export function greetingName(name: string | undefined | null): string {
  return acceptable(name?.trim());
}

function acceptable(word: string | undefined): string {
  if (!word) return "";
  const cleaned = word.replace(/[,;]+$/, "");
  if (cleaned.length < MIN_LENGTH || cleaned.length > 40) return "";
  return NAME_WORD.test(cleaned) ? cleaned : "";
}
